<template>
  <div class="vivo-wrap">
    <!-- El shadow root cuelga de acá. Vue no lo toca: se monta a mano en
         `mounted` y su contenido lo maneja este componente con DOM plano. -->
    <div ref="host" class="vivo-host"></div>
    <p v-if="error" class="vivo-error">
      No pudimos cargar la simulación. Inicia sesión y crea una planificación
      de verdad: es más rápido que verla.
    </p>
  </div>
</template>

<script>
// La simulación de /planificaciones-de-clase/ dentro del chat: el
// chat elige curso, asignatura, OA e indicador, se teclea la personalización y
// después la planificación y el PowerPoint se escriben solos, uno al lado del
// otro.
//
// Va en un SHADOW ROOT, no en la página a secas ni en un iframe.
//
// · No a secas: el marcado de adentro es el que genera el producto y trae
//   nombres como `.slide`, `.container`, `.header`, `.msg` o `.pick`, que
//   chocan de frente con los del SPA. Su hoja son 46 KB (el renderizador real,
//   copiado de DailyPlanification.vue) y soltarla en el documento repinta la
//   aplicación entera.
// · No en un iframe: el marco terminaba bloqueado.
//
// El shadow root es el aislamiento que el navegador ya trae, sin marco: el CSS
// de adentro no sale y el de afuera no entra. Lo único que cruza son las
// variables CSS —heredan a través del borde—, y por eso el endpoint manda
// también el `:root` de site.css: dentro del shadow ese selector no engancha.
//
// El HTML, la hoja y las variables los sirve
// backend/marketing/views.vivo_planificacion. Acá solo se monta y se anima.
import informationService from "@/services/informationService";
import { montarEnSombra } from "./shadowMount";
import {
  medirDemoIniciada,
  medirDemoTerminada,
} from "@/services/analytics";

// El texto que se "escribe" en la caja de personalización. Es el mismo de la
// página; vive acá porque es del guion de la animación, no del contenido.
const PERSONALIZACION =
  "Quiero mostrarles comidas saludables típicas chilenas: la cazuela, el charquicán y la marraqueta con palta.";

// El orden en que entran las láminas del PowerPoint. No es 1..12: la
// simulación muestra primero las que se ven mejor.
const LAMINAS = [4, 12, 2, 5, 6, 3];

export default {
  name: "PlanificationVivo",
  data() {
    // La simulacion se cuenta una vez por montaje. El
    // IntersectionObserver no se desconecta nunca y el boton «ver de
    // nuevo» vuelve a llamar a `correr()`: sin estas banderas,
    // `tutorial_begin` mediria scroll y repeticiones.
    return { error: false, inicioContado: false, finContado: false };
  },
  mounted() {
    informationService
      .getVivoPlanificacion()
      .then((payload) => this.montar(payload))
      .catch(() => {
        this.error = true;
      });
  },
  beforeDestroy() {
    this.parar();
  },
  methods: {
    montar(payload) {
      const montado = montarEnSombra(this.$refs.host, payload, {
        clase: "vivo",
        ajustes:
          // La simulación es lo único que hay en este árbol: sin los márgenes
          // de sección de la página y sin su sombra, que ya la pone el chat.
          ".vivo{padding:0}.vivo__demo{box-shadow:none;border:0}" +
          // La planificación y el PowerPoint van uno sobre otro, no lado a
          // lado. La hoja los apila bajo 900 px de ANCHO DE VENTANA, y dentro
          // de un shadow root esa media query mide la ventana, no la columna
          // del chat (~950 px como máximo, contra los 1.180 del sitio): en un
          // monitor grande dice "ancho" y las dos columnas salen
          // estranguladas. Apilar siempre es la presentación que la página le
          // da a una pantalla angosta, que es justo lo que esto es.
          ".vivo__split{grid-template-columns:1fr}" +
          ".vivo__plan,.vivo__slides{height:420px}",
      });
      if (!montado) {
        this.error = true;
        return;
      }
      const { raiz, cuerpo } = montado;
      this.raiz = raiz;

      // `js` es la clase con la que la hoja apaga lo que la animación va
      // encendiendo (el póster, las fichas, la vista partida). Sin ella no hay
      // nada escondido: la simulación se queda quieta y completa, que es el
      // modo correcto para quien pidió menos movimiento.
      if (
        window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
        !window.IntersectionObserver
      ) {
        return;
      }
      cuerpo.classList.add("js");
      // `getElementById` existe en el shadow root igual que en el documento.
      this.$ = (id) => raiz.getElementById(id);
      this.$("vagain").addEventListener("click", this.correr);
      // Arranca al entrar en pantalla, no al montar: el paso se añade al chat
      // y el visitante llega un momento después.
      this.observador = new IntersectionObserver(
        (entradas) => {
          entradas.forEach((e) => {
            if (e.isIntersecting) this.correr();
          });
        },
        { threshold: 0.25 }
      );
      this.observador.observe(this.$refs.host);
    },

    contarInicio() {
      if (this.inicioContado) return;
      this.inicioContado = true;
      medirDemoIniciada("Planificaciones");
    },
    contarFin() {
      if (this.finContado) return;
      this.finContado = true;
      medirDemoTerminada("Planificaciones");
    },

    // --- el animador ----------------------------------------------------
    // Port del script de la página de producto. Dos relojes, un solo
    // contenido: si allá cambian los tiempos, acá no pasa nada.
    parar() {
      (this.timers || []).forEach(clearTimeout);
      this.timers = [];
      this.trabajos = [];
      clearInterval(this.tick);
      if (this.observador) this.observador.disconnect();
    },
    en(t, fn) {
      this.timers.push(setTimeout(fn, Math.max(0, t)));
    },
    // Vacía el elemento al encolarlo: el texto viene servido dentro del HTML y
    // el tecleo tiene que partir de cero, no truncarlo de golpe.
    teclear(el, s, t, d) {
      el.textContent = "";
      this.trabajos.push({ el, s, t, d, n: 0 });
    },
    correr() {
      this.contarInicio();
      this.parar();
      this.timers = [];
      this.trabajos = [];
      const raiz = this.raiz;
      const $ = this.$;
      this.T0 = performance.now();
      this.tick = setInterval(() => {
        if (!this.trabajos.length) return;
        const ahora = performance.now() - this.T0;
        this.trabajos.forEach((j) => {
          const n = Math.min(
            j.s.length,
            Math.max(0, Math.floor((ahora - j.t) / j.d))
          );
          if (n !== j.n) {
            j.n = n;
            j.el.textContent = j.s.slice(0, n);
          }
        });
        this.trabajos = this.trabajos.filter((j) => j.n < j.s.length);
      }, 30);

      // Deja el árbol como recién cargado para poder repetir la simulación.
      const vivo = $("vivo");
      vivo.querySelectorAll(".on").forEach((e) => e.classList.remove("on"));
      vivo
        .querySelectorAll(".slide")
        .forEach((e) => e.classList.remove("lista", "dibujando"));
      $("vchat").classList.remove("gone");
      $("vsplit").classList.remove("on");
      $("vag").classList.remove("press");
      $("vpf").style.transform = "";
      $("vsf").style.transform = "";

      // El feed se desplaza para que lo último escrito quede a la vista.
      const feed = (id) => {
        const f = $(id);
        const alto = f.parentElement.clientHeight - 20;
        f.style.transform = `translateY(${Math.min(0, alto - f.scrollHeight)}px)`;
      };
      const alTope = (fid, sid) => {
        $(fid).style.transform = `translateY(${-Math.max(
          0,
          $(sid).offsetTop
        )}px)`;
      };
      const lamina = (id, t0, d, ig) => {
        const el = $(id);
        const sl = el.querySelector(".slide");
        this.en(t0, () => {
          el.classList.add("on");
          sl.classList.add("dibujando");
        });
        let t = t0 + 850;
        this.en(t, () => sl.classList.remove("dibujando"));
        const ims = el.querySelectorAll(".simg");
        ims.forEach((im, k) =>
          this.en(t0 + 1100 + k * ig, () => im.classList.add("on"))
        );
        el.querySelectorAll("[data-tx]").forEach((n) => {
          const s = n.getAttribute("data-tx");
          this.teclear(n, s, t, d);
          t += s.length * d + 80;
        });
        t = Math.max(t, t0 + 1100 + ims.length * ig) + 400;
        this.en(t, () => sl.classList.add("lista"));
        return t + 700;
      };
      const poster = (id, t0, d, fid) => {
        let t = t0;
        this.en(t0, () => $(id).classList.add("on"));
        $(id)
          .querySelectorAll(".prow")
          .forEach((r) => {
            this.en(t, () => {
              r.classList.add("on");
              feed(fid);
            });
            if (r.querySelector(".section-header")) {
              t += 500;
              return;
            }
            r.querySelectorAll("[data-tx]").forEach((n) => {
              const s = n.getAttribute("data-tx");
              this.teclear(n, s, t, d);
              t += s.length * d + 60;
            });
          });
        return t;
      };

      // El chat elige, se teclea la personalización y se abre la vista partida.
      [400, 800, 1200, 1600].forEach((t, i) =>
        this.en(t, () => $(`vp${i + 1}`).classList.add("on"))
      );
      this.en(2000, () => {
        $("vm5").classList.add("on");
        $("vtb").classList.add("on");
      });
      this.teclear($("vtt"), PERSONALIZACION, 2300, 14);
      this.en(4000, () => $("vag").classList.add("press"));
      this.en(4300, () => {
        $("vchat").classList.add("gone");
        $("vsplit").classList.add("on");
      });

      const finPoster = poster("vpl", 4500, 5, "vpf");
      for (let x = 4500; x < finPoster; x += 120) {
        this.en(x, () => feed("vpf"));
      }
      let t = 4700;
      LAMINAS.forEach((n) => {
        const t0 = t;
        t = lamina(`vsl${n}`, t0, 2, 250);
        this.en(t0 + 30, () => alTope("vsf", `vsl${n}`));
      });
      // El final de la simulacion: el ultimo temporizador, el que
      // enciende «ver de nuevo». Si manana se agrega una escena
      // despues de esta, el cierre se mueve con ella.
      this.en(Math.max(t, finPoster) + 500, () => {
        raiz.getElementById("vagain").classList.add("on");
        this.contarFin();
      });
    },
  },
};
</script>

<style scoped>
.vivo-wrap {
  width: 100%;
}

.vivo-host {
  display: block;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 24px 46px rgba(57, 62, 70, 0.15);
}

.vivo-error {
  margin: 0;
  padding: 1.2rem 1.4rem;
  color: #5c636d;
  text-align: left;
}
</style>
